import { motion } from "framer-motion";
import { MapPin, Unlock, Navigation, ScrollText, Search, Crosshair, Play, Clock } from "lucide-react";
import PhoneFrame from "@/components/site/PhoneMockup";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.15, duration: 0.6 } }),
};

const CluePhone = () => (
  <PhoneFrame testId="mockup-clue-phone">
    <div className="px-5 flex flex-col gap-4 h-full">
      <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FF453A]">Clue #1 of 8</p>
      <h4 className="font-heading font-bold text-white text-lg leading-snug">Where It Started</h4>
      <div className="rounded-2xl bg-[#131620] border border-[#2A3042] p-4">
        <ScrollText size={18} className="text-[#FF453A] mb-3" />
        <p className="text-[13px] text-[#c9cdd8] italic leading-relaxed">
          "Let's start at the beginning.<br />Literally.<br /><br />Where did you enter the world?"
        </p>
      </div>
      <div className="rounded-2xl overflow-hidden border border-[#2A3042]">
        <img
          src="https://images.pexels.com/photos/12940795/pexels-photo-12940795.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Vintage map clue"
          className="w-full h-24 object-cover opacity-80"
        />
      </div>
      <div className="mt-auto mb-6 flex flex-col gap-2.5">
        <button className="rounded-full bg-[#FF453A] text-white text-sm font-semibold py-3 flex items-center justify-center gap-2">
          <MapPin size={15} /> Drop a Pin
        </button>
        <button className="rounded-full border border-[#2A3042] text-[#9BA1B0] text-sm font-semibold py-3 flex items-center justify-center gap-2">
          <Navigation size={15} /> Check My Location
        </button>
      </div>
    </div>
  </PhoneFrame>
);

const MapPhone = () => (
  <PhoneFrame testId="mockup-map-phone">
    <div className="flex flex-col h-full">
      <div className="px-5 pb-3 flex items-center gap-2">
        <div className="flex-1 rounded-full bg-[#131620] border border-[#2A3042] px-3 py-2 flex items-center gap-2">
          <Search size={13} className="text-[#5a6070]" />
          <span className="text-[11px] text-[#5a6070]">Search the map…</span>
        </div>
        <Crosshair size={16} className="text-[#9BA1B0]" />
      </div>
      <div className="relative flex-1 bg-[#0e1119] overflow-hidden">
        <svg className="absolute inset-0 w-full h-full opacity-40" viewBox="0 0 270 400">
          <path d="M0 80 L270 60" stroke="#2A3042" strokeWidth="7" fill="none" />
          <path d="M0 180 L270 200" stroke="#2A3042" strokeWidth="10" fill="none" />
          <path d="M0 300 L270 280" stroke="#2A3042" strokeWidth="6" fill="none" />
          <path d="M70 0 L90 400" stroke="#2A3042" strokeWidth="8" fill="none" />
          <path d="M190 0 L170 400" stroke="#2A3042" strokeWidth="6" fill="none" />
          <rect x="100" y="90" width="60" height="60" fill="#131620" />
          <rect x="200" y="220" width="50" height="45" fill="#131620" />
          <rect x="20" y="210" width="40" height="55" fill="#131620" />
          <circle cx="135" cy="200" r="60" fill="#34C759" opacity="0.07" />
        </svg>
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full pin-drop">
          <MapPin size={38} className="text-[#FF453A] drop-shadow-[0_0_12px_rgba(255,69,58,0.8)]" fill="#FF453A" strokeWidth={1.5} />
        </div>
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 w-3 h-1.5 rounded-full bg-black/50 blur-[2px]" />
      </div>
      <div className="p-5 pb-7">
        <button className="w-full rounded-full bg-[#FF453A] text-white text-sm font-semibold py-3.5 glow-red">
          Lock In My Pin
        </button>
      </div>
    </div>
  </PhoneFrame>
);

const UnlockPhone = () => (
  <PhoneFrame testId="mockup-unlock-phone">
    <div className="px-5 flex flex-col gap-4 h-full items-center text-center">
      <div className="mt-4 relative">
        <span className="absolute inset-0 rounded-full bg-[#34C759]/30 pin-pulse" />
        <span className="relative flex items-center justify-center w-16 h-16 rounded-full bg-[#34C759]/15 border border-[#34C759]/40">
          <Unlock size={26} className="text-[#34C759]" />
        </span>
      </div>
      <h4 className="font-heading font-extrabold text-white text-2xl tracking-tight">UNLOCKED</h4>
      <p className="text-[11px] text-[#9BA1B0] -mt-2">71 feet from the target</p>
      <div className="w-full rounded-2xl overflow-hidden border border-[#2A3042] relative">
        <img
          src="https://images.unsplash.com/photo-1595981685360-02d21ed6afde?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
          alt="Unlocked memory"
          className="w-full h-36 object-cover"
        />
        <div className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-black/60 backdrop-blur flex items-center justify-center">
          <Play size={14} className="text-white ml-0.5" />
        </div>
      </div>
      <p className="text-[12px] text-[#c9cdd8] leading-relaxed">
        "Still one of the best days we ever had. Watch the video — I finally tell the whole story."
      </p>
      <div className="mt-auto mb-6 w-full rounded-2xl bg-[#131620] border border-[#2A3042] px-4 py-3 flex items-center justify-between">
        <span className="text-[11px] text-[#9BA1B0] flex items-center gap-1.5"><Clock size={12} /> Next clue</span>
        <span className="text-[12px] font-heading font-bold text-white">6d : 14h : 22m</span>
      </div>
    </div>
  </PhoneFrame>
);

export default function GameplayMockups() {
  const phones = [
    { el: <CluePhone />, label: "1 — Get the clue" },
    { el: <MapPhone />, label: "2 — Drop your pin (or go there)" },
    { el: <UnlockPhone />, label: "3 — Unlock the memory" },
  ];
  return (
    <div className="flex flex-col lg:flex-row items-center justify-center gap-10 lg:gap-8">
      {phones.map((p, i) => (
        <motion.div
          key={p.label}
          custom={i}
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className={`flex flex-col items-center gap-5 ${i === 1 ? "lg:-mt-10" : ""}`}
        >
          {p.el}
          <p className="text-sm font-heading font-semibold text-[#9BA1B0]">{p.label}</p>
        </motion.div>
      ))}
    </div>
  );
}
